// The Firmware page's state — loading, filtering, selecting, sending.
//
// Both shells mount this; only the table markup differs. Nothing here touches
// the DOM, so the desktop table and the mobile list cannot disagree about which
// devices are selected or what "needs update" means.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collectEstateDevices, loadFirmwareFacts, fleetCounts,
  FLEET_LIVE, CURRENT, STALE, UNKNOWN_STATE,
} from '../devices/firmwareFleet';
import { runFirmwareUpdate, previewPayload } from '../devices/firmwareUpdate';

export const FILTER_ALL = 'all';
export const FILTER_NEEDS = 'needs';
export const FILTER_CURRENT = 'current';
export const FILTER_UNKNOWN = 'unknown';
export const FILTER_OFFLINE = 'offline';

/** Bucket for devices whose firmware file could not be resolved. */
export const NO_BIN_FILE = '(no file resolved)';

export const STATUS_FILTERS = [
  { key: FILTER_ALL, label: 'All' },
  { key: FILTER_NEEDS, label: 'Needs update' },
  { key: FILTER_UNKNOWN, label: 'Not known' },
  { key: FILTER_CURRENT, label: 'Up to date' },
  { key: FILTER_OFFLINE, label: 'Offline' },
];

export function useFirmwareFleet(userId) {
  // idle -> locations -> devices -> firmware -> ready, plus sending/done.
  const [phase, setPhase] = useState('idle');
  const [progress, setProgress] = useState({ done: 0, total: 0, label: '' });
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(0);

  const [statusFilter, setStatusFilter] = useState(FILTER_ALL);
  const [locationFilter, setLocationFilter] = useState('');   // '' = every location
  const [binFilter, setBinFilter] = useState('');             // '' = every .bin
  const [liveOnly, setLiveOnly] = useState(false);
  const [queueOffline, setQueueOffline] = useState(false);
  const [queuedCount, setQueuedCount] = useState(0);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => new Set());

  // Set when the user leaves or presses Stop. Checked between requests, so a
  // 1300-call load does not carry on behind a closed screen.
  const stopRef = useRef(false);
  useEffect(() => () => { stopRef.current = true; }, []);

  const load = useCallback(async () => {
    stopRef.current = false;
    setError(null);
    setSelected(new Set());
    setRows([]);
    setSent(0);
    try {
      setPhase('devices');
      const devices = await collectEstateDevices(
        (p) => setProgress(p),
        () => stopRef.current,
      );
      if (stopRef.current) { setPhase('idle'); return; }
      // Shown immediately, before the firmware pass: the estate list is the
      // slow part's INPUT, and staring at a spinner for another minute with
      // nothing on screen reads as a hang. Rows appear with their liveness
      // filled in and their firmware columns still blank.
      setRows(devices);
      setPhase('firmware');
      const enriched = await loadFirmwareFacts(
        devices,
        (p) => setProgress(p),
        () => stopRef.current,
      );
      if (stopRef.current) { setRows(enriched); setPhase('ready'); return; }
      setRows(enriched);
      setPhase('ready');
    } catch (e) {
      setError(e?.message || 'Could not load the device list.');
      setPhase('idle');
    }
  }, []);

  const stop = useCallback(() => { stopRef.current = true; }, []);

  const locations = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => { if (!m.has(r.locationName)) m.set(r.locationName, 0); });
    rows.forEach((r) => m.set(r.locationName, m.get(r.locationName) + 1));
    return [...m.entries()].map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  /**
   * The .bin files actually IN USE, with how many devices are on each and how
   * many of those are behind.
   *
   * ⚠️ DERIVED FROM THE LOADED ROWS, not from `GetAllOTABinFile`. That endpoint
   * exists and returns the 15-row catalog, and it is the wrong source here for
   * two reasons: it lists files no installed device uses, and it carries no
   * date and no counts. What the operator wants to know is "which builds is my
   * estate actually on" — a question only the devices can answer.
   *
   * Devices whose detail lookup failed carry no file at all. They are grouped
   * under a named bucket rather than dropped, because a device with no
   * resolvable firmware is a thing worth being able to select for.
   */
  const binFiles = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      const key = r.binFile || NO_BIN_FILE;
      if (!m.has(key)) m.set(key, { name: key, count: 0, needsUpdate: 0, builtAt: null });
      const e = m.get(key);
      e.count += 1;
      if (r.state === STALE) e.needsUpdate += 1;
      // Every device on one file reports the same build stamp, so the first
      // non-null is the file's date — shown in the dropdown so the operator can
      // tell an old build from a current one without opening a row.
      if (e.builtAt == null && r.builtAt != null) e.builtAt = r.builtAt;
    });
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const counts = useMemo(() => fleetCounts(rows), [rows]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (locationFilter && r.locationName !== locationFilter) return false;
      if (binFilter && (r.binFile || NO_BIN_FILE) !== binFilter) return false;
      if (liveOnly && r.liveState !== FLEET_LIVE) return false;
      if (statusFilter === FILTER_NEEDS && r.state !== STALE) return false;
      if (statusFilter === FILTER_CURRENT && r.state !== CURRENT) return false;
      if (statusFilter === FILTER_UNKNOWN && r.state !== UNKNOWN_STATE) return false;
      if (statusFilter === FILTER_OFFLINE && r.liveState === FLEET_LIVE) return false;
      if (q && !`${r.deviceName} ${r.uno} ${r.locationName} ${r.roomName}`.toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [rows, statusFilter, locationFilter, binFilter, liveOnly, search]);

  // Counts for what is ON SCREEN, not for the estate. Filtering to one .bin
  // file and still reading the global "needs update 214" answers a question
  // nobody asked — the point of picking a file is to ask about that file.
  const visibleCounts = useMemo(() => fleetCounts(visible), [visible]);

  // ⚠️ ONLY WHAT IS ON SCREEN CAN BE SENT TO. A row that is selected and then
  // filtered out of view would otherwise be reflashed without ever having been
  // shown — an invisible instruction, on the one screen whose whole job is
  // making the instruction visible. Same rule as UpdateCodeDialog.
  const onScreen = useMemo(() => new Set(visible.map((r) => r.uno)), [visible]);

  // ── Three routes, and only one of them needs the device listening ────────
  //
  // MQTT reaches a device that is connected NOW; a publish to one that is not
  // is dropped silently, which is why offline devices were unselectable.
  //
  // The two server-side routes reach the others — the ~20 minute poll and the
  // `RequireUpdateCode` mark read on every Wi-Fi connect. See
  // core/devices/firmwareUpdate.js for what each is worth; both are set on
  // every selected device by `runFirmwareUpdate`.
  //
  // ⚠️ THE POLL IS CONFIRMED ON THE ESP8266 TOO, and this comment used to say
  // it was not: `OtaForSwitching.ino:1591` is
  // `millis() > LastGetPendingCommandTime + 1200000`, the same twenty minutes
  // the ESP32-C3 uses. The opt-in below therefore now governs only whether
  // offline devices are worth listing on a fleet screen, not whether reaching
  // them works.
  const selectable = useMemo(
    () => visible.filter((r) => queueOffline || r.liveState === FLEET_LIVE),
    [visible, queueOffline],
  );

  const chosen = useMemo(
    () => rows.filter((r) => selected.has(r.uno) && onScreen.has(r.uno)),
    [rows, selected, onScreen],
  );

  // Live ones go over MQTT and start immediately; the rest are queued. Split
  // here rather than in the screen so both shells cannot disagree about which
  // device took which route.
  const targets = useMemo(
    () => chosen.filter((r) => r.liveState === FLEET_LIVE),
    [chosen],
  );
  const queued = useMemo(
    () => (queueOffline ? chosen.filter((r) => r.liveState !== FLEET_LIVE) : []),
    [chosen, queueOffline],
  );

  const toggle = useCallback((uno) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(uno)) next.delete(uno); else next.add(uno);
    return next;
  }), []);

  const allSelected = selectable.length > 0 && selectable.every((r) => selected.has(r.uno));
  const someSelected = selectable.some((r) => selected.has(r.uno));

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      const ids = selectable.map((r) => r.uno);
      const on = !(ids.length > 0 && ids.every((u) => prev.has(u)));
      const next = new Set(prev);
      ids.forEach((u) => (on ? next.add(u) : next.delete(u)));
      return next;
    });
  }, [selectable]);

  /**
   * Tick every live device on screen that is not already on this build.
   *
   * UNKNOWN is included, deliberately — see loadFirmwareFacts: a failed flash
   * still stamps CodeUpdated, so "don't know" must not quietly mean "skip".
   */
  const selectNeeding = useCallback(() => {
    setSelected(new Set(
      selectable.filter((r) => r.state !== CURRENT).map((r) => r.uno),
    ));
  }, [selectable]);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  const run = useCallback(async () => {
    if (!chosen.length) return;
    setPhase('sending');
    setSent(0);
    // Row writes first, then the spaced MQTT walk — a run abandoned half way
    // through has still instructed every device by the durable routes. See
    // runFirmwareUpdate.
    const out = await runFirmwareUpdate(chosen, targets, userId, (p) => setSent(p.sent || 0));
    setQueuedCount(out.marked);
    setSent(out.sent);
    setPhase('done');
  }, [chosen, targets, userId]);

  const backToList = useCallback(() => setPhase('ready'), []);

  return {
    phase,
    progress,
    rows,
    visible,
    locations,
    binFiles,
    counts,
    visibleCounts,
    error,
    load,
    stop,
    // filters
    statusFilter,
    setStatusFilter,
    locationFilter,
    setLocationFilter,
    binFilter,
    setBinFilter,
    liveOnly,
    setLiveOnly,
    queueOffline,
    setQueueOffline,
    search,
    setSearch,
    // selection
    selected,
    toggle,
    toggleAll,
    allSelected,
    someSelected,
    selectable,
    selectNeeding,
    clearSelection,
    targets,
    queued,
    queuedCount,
    // send
    run,
    sent,
    backToList,
    payload: previewPayload(null, userId),
  };
}

export default useFirmwareFleet;
